/**
 * ScreenQueueCard — Redesigned triage card for the screening queue
 * 
 * Layout:
 * 1. Title + metadata (journal, specialty, pub date)
 * 2. Truncated abstract → expand/collapse
 * 3. Truncated AI summary → expand/collapse
 * 4. Action buttons: Save to Library, Skip (blue theme)
 * 5. Dropdown menu with Run Queue Now, Suppress, Delete options
 * 
 * Alternating card shades for visual distinction.
 */
import React, { useState } from 'react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { useNavV2 } from '../../../hooks/useNavV2';
import { CrossAppLinks } from '../../CrossAppLinks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { ScreenQueueArticle } from '../../../api';
import {
  BookmarkPlus,
  BookmarkCheck,
  Clock,
  SkipForward,
  Loader2,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  Sparkles,
  BookOpen,
  ExternalLink,
  MoreVertical,
  Play,
  Pause,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

// Suppress duration options
const SUPPRESS_UNITS = ['days', 'weeks', 'months'];

interface ScreenQueueCardProps {
  article: ScreenQueueArticle;
  index?: number;
  isSelected: boolean;
  isProcessing: boolean;
  onSelect: () => void;
  onSave: () => void;
  onDefer: () => void;
  onSkip: () => void;
  onRunDigestNow?: (profileId: string) => Promise<void>;
  onSuppressDigest?: (profileId: string, duration: { value: number; unit: string }) => Promise<void>;
  onDeleteDigest?: (profileId: string) => Promise<void>;
  profileId?: string;
}

const TRUNCATE_LEN = 260; // ~3-4 lines of text

function truncateText(text: string | undefined, maxLen = TRUNCATE_LEN) {
  if (!text) return { display: '', full: '', needsExpand: false };
  if (text.length <= maxLen) return { display: text, full: text, needsExpand: false };
  const cut = text.lastIndexOf(' ', maxLen);
  return {
    display: text.slice(0, cut > 0 ? cut : maxLen) + '...',
    full: text,
    needsExpand: true,
  };
}

const CARD_BG = [
  'bg-white',
  'bg-slate-50/70',
  'bg-gray-50/60',
  'bg-zinc-50/50',
];

export const ScreenQueueCard: React.FC<ScreenQueueCardProps> = ({
  article,
  index = 0,
  isSelected,
  isProcessing,
  onSelect,
  onSave,
  onDefer: _onDefer,
  onSkip,
  onRunDigestNow,
  onSuppressDigest,
  onDeleteDigest,
  profileId,
}) => {
  const [abstractExpanded, setAbstractExpanded] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  // Dialog states
  const [suppressDialogOpen, setSuppressDialogOpen] = useState(false);
  const navV2 = useNavV2();
  const [suppressValue, setSuppressValue] = useState(1);
  const [suppressUnit, setSuppressUnit] = useState('days');
  const [isSuppressing, setIsSuppressing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRunningDigest, setIsRunningDigest] = useState(false);

  // Swipe gesture state
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const touchStartRef = React.useRef<{ x: number; y: number; time: number } | null>(null);
  const SWIPE_THRESHOLD = 80;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() };
    setSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;
    // Only track horizontal swipes (ignore vertical scroll)
    if (Math.abs(dx) > Math.abs(dy) * 1.5) {
      setSwipeX(dx);
    }
  };

  const handleTouchEnd = () => {
    if (swipeX > SWIPE_THRESHOLD) {
      onSave(); // Swipe right = Save
    } else if (swipeX < -SWIPE_THRESHOLD) {
      onSkip(); // Swipe left = Skip
    }
    setSwipeX(0);
    setSwiping(false);
    touchStartRef.current = null;
  };

  const swipeDirection = swipeX > 30 ? 'right' : swipeX < -30 ? 'left' : null;

  const handleRunDigestNow = async () => {
    if (onRunDigestNow && profileId) {
      setIsRunningDigest(true);
      try {
        await onRunDigestNow(profileId);
        toast.success('Queue generation started!');
      } catch (error) {
        toast.error('Failed to run queue');
      } finally {
        setIsRunningDigest(false);
      }
    }
  };

  const handleConfirmSuppress = async () => {
    if (onSuppressDigest && profileId) {
      setIsSuppressing(true);
      try {
        await onSuppressDigest(profileId, { value: suppressValue, unit: suppressUnit });
        toast.success(`Queue suppressed for ${suppressValue} ${suppressUnit}`);
        setSuppressDialogOpen(false);
      } catch (error) {
        toast.error('Failed to suppress queue');
      } finally {
        setIsSuppressing(false);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (onDeleteDigest && profileId) {
      setIsDeleting(true);
      try {
        await onDeleteDigest(profileId);
        toast.success('Queue profile deleted');
        setDeleteDialogOpen(false);
      } catch (error) {
        toast.error('Failed to delete queue');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const formattedDate = article.pub_date
    ? new Date(article.pub_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  const hasAbstract = article.abstract &&
    article.abstract.toLowerCase() !== 'no abstract available' &&
    article.abstract.length > 20;

  const hasAiSummary = article.ai_summary &&
    !article.ai_summary.toLowerCase().includes('not available') &&
    article.ai_summary.length > 20;

  const abstractInfo = truncateText(article.abstract);
  const summaryInfo = truncateText(article.ai_summary);

  const isSaved = article.screening_status === 'keep' || article.is_in_library;
  const isDeferred = article.screening_status === 'later';
  const isSkipped = article.screening_status === 'skip';
  const pubmedUrl = `https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`;

  const shade = isSaved
    ? 'bg-sky-50/40'
    : isDeferred
    ? 'bg-amber-50/30'
    : isSkipped
    ? 'bg-slate-100/60 opacity-60'
    : CARD_BG[index % CARD_BG.length];

  return (
    <div className="relative overflow-hidden rounded-xl">
      <div
        className={`rounded-xl border transition-all ${shade} ${
          isSelected
            ? 'border-sky-400 ring-2 ring-sky-200/60 shadow-md'
            : 'border-slate-200/80 hover:border-sky-300/60 hover:shadow-sm'
        }`}
        onClick={onSelect}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        data-testid={`screen-card-${article.pmid}`}
      >
      {/* ─── Row 1: Title + Metadata ─── */}
      <div className="px-5 pt-4 pb-2">
        <h3
          className={`responsive-title font-semibold text-base leading-snug line-clamp-2 ${
            isSelected ? 'text-sky-800' : 'text-slate-900'
          }`}
          data-testid={`screen-card-title-${article.pmid}`}
        >
          {article.title}
        </h3>

        <div className="flex items-center gap-2 mt-1.5 flex-wrap text-sm">
          {article.journal && (
            <span className="font-medium text-sky-700">{article.journal}</span>
          )}
          {formattedDate && (
            <span className="text-slate-500">· {formattedDate}</span>
          )}
          <a
            href={pubmedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-400 hover:text-sky-600 flex items-center gap-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            PMID: {article.pmid}
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>

        {/* Tags */}
        {((article.design_tags?.length ?? 0) > 0 || (article.topic_tags?.length ?? 0) > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {article.design_tags?.[0] && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {article.design_tags[0]}
              </Badge>
            )}
            {article.topic_tags?.slice(0, 3).map((tag: string, i: number) => (
              <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Status badge for already-screened cards */}
        {(isSaved || isDeferred || isSkipped) && (
          <div className="mt-2">
            <Badge
              variant="outline"
              className={`text-xs px-2 py-0.5 ${
                isSaved
                  ? 'text-sky-700 border-sky-200 bg-sky-50'
                  : isDeferred
                  ? 'text-amber-700 border-amber-200 bg-amber-50'
                  : 'text-slate-500 border-slate-200 bg-slate-50'
              }`}
            >
              {isSaved && <BookmarkCheck className="w-3 h-3 mr-1" />}
              {isDeferred && <Clock className="w-3 h-3 mr-1" />}
              {isSkipped && <SkipForward className="w-3 h-3 mr-1" />}
              {isSaved ? 'Saved' : isDeferred ? 'Deferred' : 'Skipped'}
            </Badge>
          </div>
        )}
      </div>

      {/* ─── Row 2: Abstract (truncated with expand) ─── */}
      {hasAbstract && (
        <div className="px-5 py-2.5 border-t border-slate-100">
          <button
            onClick={(e) => { e.stopPropagation(); setAbstractExpanded(!abstractExpanded); }}
            className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors"
            data-testid={`toggle-abstract-${article.pmid}`}
          >
            <BookOpen className="h-3 w-3" /> Abstract
            {abstractInfo.needsExpand && (
              abstractExpanded
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />
            )}
          </button>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
            {abstractExpanded ? abstractInfo.full : abstractInfo.display}
          </p>
        </div>
      )}

      {/* ─── Row 3: AI Summary (truncated with expand) ─── */}
      {hasAiSummary && (
        <div className="px-5 py-2.5 border-t border-slate-100 bg-sky-50/30">
          <button
            onClick={(e) => { e.stopPropagation(); setSummaryExpanded(!summaryExpanded); }}
            className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-sky-500 hover:text-sky-700 transition-colors"
            data-testid={`toggle-summary-${article.pmid}`}
          >
            <Sparkles className="h-3 w-3" /> AI Summary
            {summaryInfo.needsExpand && (
              summaryExpanded
                ? <ChevronUp className="h-3 w-3" />
                : <ChevronDown className="h-3 w-3" />
            )}
          </button>
          <p className="text-sm text-slate-700 leading-relaxed">
            {summaryExpanded ? summaryInfo.full : summaryInfo.display}
          </p>
        </div>
      )}

      {/* No abstract/summary */}
      {!hasAbstract && !hasAiSummary && (
        <div className="border-t border-slate-100 px-5 py-2.5">
          <p className="text-sm text-slate-400 italic">Abstract not available for this article.</p>
        </div>
      )}

      {/* ─── Row 4: Action Buttons ─── */}
      <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100">
        {!isSaved && !isSkipped && (
          <>
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onSave(); }}
              disabled={isProcessing}
              className="h-8 px-3.5 bg-sky-600 hover:bg-sky-700 text-white text-xs shadow-sm"
              data-testid={`save-${article.pmid}`}
            >
              {isProcessing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
              ) : (
                <BookmarkPlus className="w-3.5 h-3.5 mr-1.5" />
              )}
              Save to Library
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onSkip(); }}
              disabled={isProcessing}
              className="h-8 px-3.5 text-xs text-slate-600 border-slate-200 hover:bg-slate-50"
              data-testid={`skip-btn-${article.pmid}`}
            >
              <SkipForward className="w-3.5 h-3.5 mr-1.5" />
              Skip
            </Button>
          </>
        )}

        {/* Deferred state */}
        {isDeferred && !isSaved && (
          <Button
            size="sm"
            onClick={(e) => { e.stopPropagation(); onSave(); }}
            disabled={isProcessing}
            className="h-8 px-3.5 bg-sky-600 hover:bg-sky-700 text-white text-xs shadow-sm"
            data-testid={`save-deferred-${article.pmid}`}
          >
            {isProcessing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <BookmarkPlus className="w-3.5 h-3.5 mr-1.5" />
            )}
            Save to Library
          </Button>
        )}

        {/* Saved state */}
        {isSaved && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-3.5 text-xs text-sky-600 border-sky-200 hover:bg-sky-50"
            data-testid={`learn-${article.pmid}`}
          >
            <GraduationCap className="w-3.5 h-3.5 mr-1.5" />
            Open in Learn
          </Button>
        )}

        {/* Spacer */}
        <div className="flex-1" />
        
        {/* Run Queue Now + More options */}
        <div className="flex items-center gap-2">
          {onRunDigestNow && profileId && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); handleRunDigestNow(); }}
              disabled={isRunningDigest}
              className="h-8 px-2.5 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              data-testid={`run-digest-card-${article.pmid}`}
            >
              {isRunningDigest ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Play className="w-3.5 h-3.5" />
              )}
            </Button>
          )}
          
          {/* Dropdown menu with Suppress/Delete */}
          {(onSuppressDigest || onDeleteDigest) && profileId && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {onRunDigestNow && (
                  <DropdownMenuItem onClick={handleRunDigestNow}>
                    <Play className="h-4 w-4 mr-2" />
                    Run Queue Now
                  </DropdownMenuItem>
                )}
                {onSuppressDigest && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setSuppressDialogOpen(true)}>
                      <Pause className="h-4 w-4 mr-2" />
                      Suppress Queue
                    </DropdownMenuItem>
                  </>
                )}
                {onDeleteDigest && (
                  <DropdownMenuItem 
                    onClick={() => setDeleteDialogOpen(true)}
                    className="text-rose-600 focus:text-rose-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Queue
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* Keyboard hints */}
          {!isSaved && !isSkipped && (
            <div className="hidden md:flex items-center gap-3 text-[10px] text-slate-400 ml-2">
              <span><kbd className="px-1 py-0.5 rounded bg-slate-100 font-mono text-[10px]">S</kbd> Save</span>
              <span><kbd className="px-1 py-0.5 rounded bg-slate-100 font-mono text-[10px]">X</kbd> Skip</span>
            </div>
          )}
        </div>
      </div>

      <CrossAppLinks
        pmid={article.pmid}
        profileId={profileId || navV2.profileId}
        originApp="screen"
        title={article.title}
        className="rounded-b-xl"
      />

      {/* Suppress Queue Dialog */}
      <Dialog open={suppressDialogOpen} onOpenChange={setSuppressDialogOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Suppress Queue</DialogTitle>
            <DialogDescription>
              Pause scheduled queue generation for this profile. You can resume it anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-3 py-4">
            <span className="text-sm text-slate-600">Suppress for</span>
            <select
              value={suppressValue}
              onChange={(e) => setSuppressValue(parseInt(e.target.value))}
              className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <select
              value={suppressUnit}
              onChange={(e) => setSuppressUnit(e.target.value)}
              className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
            >
              {SUPPRESS_UNITS.map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuppressDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSuppress} disabled={isSuppressing}>
              {isSuppressing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Suppress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Queue Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Delete Queue Profile</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this digest profile? This action cannot be undone.
              This will free up a slot for a new digest.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
    </div>
  );
};

export default ScreenQueueCard;

